import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DataGrid } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import MDTypography from 'components/MDTypography';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { useNotification } from 'hook/useNotification';
import { useGetRolesQuery } from 'services/roleApi';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

/**
 * Component for displaying and managing the details of a User. Allows viewing,
 * creating, and updating Users.
 *
 * @component
 *
 * @example
 * <UserDetails user={user} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.user - The User object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The User Details component.
 */
function UserDetails({ user, onSubmit, onFieldChange }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const { data: roles = [], isLoading: roleLoading, isError: roleError } = useGetRolesQuery();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      login: '',
      password: '',
      firstname: '',
      lastname: '',
      disabled: false,
      description: '',
      roles: []
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetUser = useCallback(() => {
    if (user?.login) {
      reset(user);
    } else {
      reset({
        id: '',
        login: '',
        password: '',
        firstname: '',
        lastname: '',
        disabled: false,
        description: '',
        roles: []
      });
    }
    setRowSelectionModel(user?.roles.map((role) => role.id) ?? []);
  }, [user, reset]);

  useEffect(() => {
    resetUser();
  }, [user, resetUser]);

  const clearField = (field) => {
    setValue(field, '');
  };

  const handleReset = () => {
    resetUser();
  };

  const handleRoleSelectionChange = (selectionModel) => {
    const selectedRoles = selectionModel.map((id) => roles.find((role) => role.id === id));
    reset((prevValues) => ({
      ...prevValues,
      roles: selectedRoles
    }));
    setValue('roles', selectedRoles);
  };

  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    const selectedRoles = newRowSelectionModel.map((id) => roles.find((role) => role.id === id));
    setRowSelectionModel(newRowSelectionModel);
    setValue('roles', selectedRoles);
  };

  const isCreating = !user?.id;

  if (roleError) {
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.users.role.fetch.error' })
      });
    } catch {
      // Ignore exception
    }
  }

  if (roleLoading) {
    return <IndeterminateLoading />;
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <PersonIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.users.details.creation.title'
              : 'page.users.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}
        <Controller
          name="login"
          control={control}
          rules={{ required: msg({ id: 'field.login.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.login.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.login}
              helperText={errors.login ? errors.login.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('login')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'new-password',
                  'aria-autocomplete': 'both'
                }
              }}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          defaultValue="fake_password" // Valeur par défaut pour le mot de passe
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.password.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="password" // Type password pour masquer le texte
              error={!!errors.password}
              helperText={errors.password ? errors.password.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('password')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'new-password',
                  'aria-autocomplete': 'both'
                }
              }}
            />
          )}
        />
        <Controller
          name="firstname"
          control={control}
          rules={{ required: msg({ id: 'field.firstname.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.firstname.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.firstname}
              helperText={errors.firstname ? errors.firstname.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('firstname')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="lastname"
          control={control}
          rules={{ required: msg({ id: 'field.lastname.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.lastname.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.lastname}
              helperText={errors.lastname ? errors.lastname.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('lastname')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              value={field.value ?? ''}
              label={msg({ id: 'field.description.label' })}
              variant="filled"
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('description')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <FormControl variant="filled" fullWidth error={!!errors.disabled}>
          <FormControlLabel
            control={
              <Controller
                name="disabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    color="primary"
                    sx={{ ml: '0.5rem' }}
                  />
                )}
              />
            }
            label={msg({ id: 'field.disabled.label' })}
          />
          {errors.disabled && <FormHelperText>{errors.disabled.message}</FormHelperText>}
        </FormControl>
        <FormControl fullWidth sx={{ mt: '16px !important' }}>
          <FormLabel component="legend" id="roles-label" sx={{ fontSize: 'medium' }}>
            {msg({ id: 'field.roles.label' })}
          </FormLabel>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <DataGrid
                {...field}
                rows={roles}
                columns={[
                  { field: 'id', headerName: 'ID', width: 50, resizable: false },
                  { field: 'code', headerName: 'Code', width: 200, resizable: false },
                  { field: 'label', headerName: 'Label', width: 300, resizable: false }
                ]}
                initialState={{
                  density: 'compact'
                }}
                checkboxSelection
                rowHeight={38}
                loading={roleLoading}
                onSelectionModelChange={handleRoleSelectionChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                disableColumnResize
                hideFooter
              />
            )}
          />
        </FormControl>
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

UserDetails.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    login: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    disabled: PropTypes.bool,
    description: PropTypes.string,
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string
      })
    )
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default UserDetails;
